import React, { useState, useEffect } from 'react';
import {
  Mail,
  MessageSquare,
  Smartphone,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCw,
  Send,
  AlertTriangle,
  Search,
  Filter,
  Info,
  ShieldCheck,
  ExternalLink,
  ChevronRight,
  Database,
  Radio,
  Server,
  Zap,
} from 'lucide-react';
import {
  fetchAdminNotificationDiagnostic,
  fetchAdminNotificationOverview,
  fetchAdminNotifications,
  retryAdminNotification,
  sendAdminTestEmail,
  sendAdminTestSms,
  sendAdminTestCustomerEvent,
} from '../../lib/supabaseService';
import type { NotificationRecord, NotificationOverviewMetrics } from '../../types';

export const AdminNotificationSection: React.FC = () => {
  const [diagnostic, setDiagnostic] = useState<any>(null);
  const [metrics, setMetrics] = useState<NotificationOverviewMetrics | null>(null);
  const [records, setRecords] = useState<NotificationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [actionPendingId, setActionPendingId] = useState<string | null>(null);
  const [selectedRecordForDetail, setSelectedRecordForDetail] = useState<NotificationRecord | null>(null);

  // Test state
  const [testEmail, setTestEmail] = useState('');
  const [testEmailName, setTestEmailName] = useState('Chief Admin');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<any>(null);

  const [testPhone, setTestPhone] = useState('');
  const [testPhoneName, setTestPhoneName] = useState('Artisan Lead');
  const [testPhoneLoading, setTestPhoneLoading] = useState(false);
  const [testPhoneResult, setTestPhoneResult] = useState<any>(null);

  // Customer Lifecycle Event Test State
  const [testCustEventType, setTestCustEventType] = useState<string>('NEW_QUOTE_RECEIVED');
  const [testCustEmail, setTestCustEmail] = useState('customer@jobmatch.ng');
  const [testCustPhone, setTestCustPhone] = useState('08031112233');
  const [testCustName, setTestCustName] = useState('Tunde Adeleke');
  const [testCustLoading, setTestCustLoading] = useState(false);
  const [testCustResult, setTestCustResult] = useState<any>(null);

  const loadData = async () => {
    setIsRefreshing(true);
    try {
      const [diagRes, overviewRes, listRes] = await Promise.all([
        fetchAdminNotificationDiagnostic(),
        fetchAdminNotificationOverview(),
        fetchAdminNotifications({
          channel: selectedChannel,
          status: selectedStatus,
          search: searchQuery,
          limit: 100,
        }),
      ]);

      if (diagRes) {
        setDiagnostic(diagRes);
      }
      if (overviewRes && overviewRes.success) {
        setMetrics(overviewRes);
      }
      if (listRes && listRes.success) {
        setRecords(listRes.records);
      }
    } catch (err) {
      console.error('Error loading notification telemetry:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedChannel, selectedStatus]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadData();
  };

  const handleRetry = async (recordId: string) => {
    setActionPendingId(recordId);
    try {
      const res = await retryAdminNotification(recordId);
      if (res.success) {
        alert('Notification retry succeeded and message was dispatched.');
      } else {
        alert(`Retry completed: ${res.error || 'Provider did not accept payload. Verify Render environment keys.'}`);
      }
      await loadData();
    } catch (err: any) {
      alert(`Retry error: ${err.message}`);
    } finally {
      setActionPendingId(null);
    }
  };

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail) return;
    setTestEmailLoading(true);
    setTestEmailResult(null);
    try {
      const res = await sendAdminTestEmail(testEmail, testEmailName);
      setTestEmailResult(res);
      await loadData();
    } catch (err: any) {
      setTestEmailResult({ success: false, errorMessage: err.message });
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleSendTestSms = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testPhone) return;
    setTestPhoneLoading(true);
    setTestPhoneResult(null);
    try {
      const res = await sendAdminTestSms(testPhone, testPhoneName);
      setTestPhoneResult(res);
      await loadData();
    } catch (err: any) {
      setTestPhoneResult({ success: false, errorMessage: err.message });
    } finally {
      setTestPhoneLoading(false);
    }
  };

  const handleSendTestCustomerEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testCustEventType) return;
    setTestCustLoading(true);
    setTestCustResult(null);
    try {
      const res = await sendAdminTestCustomerEvent({
        eventType: testCustEventType,
        email: testCustEmail,
        phone: testCustPhone,
        name: testCustName,
      });
      setTestCustResult(res);
      await loadData();
    } catch (err: any) {
      setTestCustResult({ success: false, error: err.message });
    } finally {
      setTestCustLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Header Card */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
              Production Notification Engine
            </span>
            <span className="text-xs text-slate-400">• Email • SMS • WhatsApp</span>
          </div>
          <h2 className="text-xl font-black text-slate-900 mt-1">Multi-Channel Operations Center</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Automated alerts dispatched to matched artisans when customers create verified jobs across Nigeria.
          </p>
        </div>

        <button
          onClick={loadData}
          disabled={isRefreshing}
          className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all shrink-0 min-h-[40px]"
        >
          <RotateCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>{isRefreshing ? 'Refreshing...' : 'Refresh Telemetry'}</span>
        </button>
      </div>

      {/* 2. Live Gateway & Connectivity Health Diagnostic Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gateway 1: SMS (SMSLive247) */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                  <Smartphone className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-900">SMSLive247 Gateway</h4>
                  <span className="text-[10px] text-slate-400 font-mono">REST API v4 Gateway</span>
                </div>
              </div>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  diagnostic?.sms?.providerConnected
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-rose-100 text-rose-800'
                }`}
              >
                {diagnostic?.sms?.providerConnected
                  ? 'Connected & Operational'
                  : 'Action Required'}
              </span>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">SMSLive247 Live Balance:</span>
                <span className="font-mono font-bold text-slate-900">{diagnostic?.sms?.balance || 'Checking...'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Configured Sender ID:</span>
                <span className="font-mono font-bold text-slate-800">
                  "{diagnostic?.sms?.senderId || 'JobMatchNG'}"
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">API Gateway:</span>
                <span className="font-mono text-[11px] text-slate-700">api.smslive247.com/api/v4</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Nigerian Networks:</span>
                <span className="text-[11px] font-medium text-emerald-700">MTN, Airtel, Glo, 9mobile ✓</span>
              </div>
            </div>
          </div>

          {/* Context Banner */}
          {!diagnostic?.sms?.providerConnected && (
            <div className="mt-3 p-2.5 rounded-xl bg-amber-50/80 border border-amber-200 text-[11px] text-amber-900">
              <div className="font-bold flex items-center gap-1 mb-0.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span>SMSLive247 Configuration Required:</span>
              </div>
              <p className="leading-snug text-[10px] text-amber-800">
                Set <code className="bg-amber-100 px-1 py-0.5 rounded font-mono">SMSLIVE247_API_KEY</code> in your environment variables to enable live Nigerian SMS delivery.
              </p>
            </div>
          )}
        </div>

        {/* Gateway 2: Email (Resend / SendGrid / Postmark / Brevo) */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-900">Transactional Email</h4>
                  <span className="text-[10px] text-slate-400 font-mono capitalize">
                    Provider: {diagnostic?.email?.provider || 'Resend'}
                  </span>
                </div>
              </div>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  diagnostic?.email?.status === 'CONFIGURED'
                    ? 'bg-emerald-100 text-emerald-800'
                    : diagnostic?.email?.status === 'KEY_MISMATCH'
                    ? 'bg-amber-100 text-amber-900 border border-amber-300'
                    : 'bg-slate-100 text-slate-700'
                }`}
              >
                {diagnostic?.email?.status === 'CONFIGURED'
                  ? 'Configured & Ready'
                  : diagnostic?.email?.status === 'KEY_MISMATCH'
                  ? 'Resend Key (re_) Needed'
                  : 'API Key Required'}
              </span>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Environment Mode:</span>
                <span className="font-semibold text-slate-800 capitalize flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${diagnostic?.email?.environment === 'production' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                  {diagnostic?.email?.environment === 'production' ? 'Production' : 'Development / Testing'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">API Key Status:</span>
                <span className="font-mono font-bold text-slate-900">
                  {diagnostic?.email?.apiKeyMasked || (diagnostic?.email?.configured ? 'Configured' : 'Missing')}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">From Address:</span>
                <span className="font-mono text-slate-800 text-[11px] truncate max-w-[170px]">
                  {diagnostic?.email?.fromAddress || 'noreply@jobmatch.ng'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Sender Display Name:</span>
                <span className="font-semibold text-slate-800">{diagnostic?.email?.fromName || 'JobMatch NG'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Supported Providers:</span>
                <span className="text-[11px] text-slate-600">Resend, SendGrid, Postmark, Brevo</span>
              </div>
            </div>
          </div>

          <div className="mt-3 p-2.5 rounded-xl bg-indigo-50/80 border border-indigo-100 text-[11px] text-indigo-900">
            <div className="font-bold flex items-center gap-1 mb-0.5">
              <ShieldCheck className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span>Verified Sender Active:</span>
            </div>
            <p className="leading-snug text-[10px] text-indigo-800">
              Outbound transactional emails are dispatched via verified domain ({diagnostic?.email?.fromAddress || 'noreply@jobmatch.ng'}).
            </p>
          </div>
        </div>

        {/* Gateway 3: Authoritative Persistence */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                  <Database className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-900">Authoritative Database</h4>
                  <span className="text-[10px] text-slate-400 font-mono">PostgreSQL + Realtime</span>
                </div>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                Supabase Authoritative
              </span>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Store Type:</span>
                <span className="font-semibold text-slate-900">Supabase PostgreSQL</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Fail-Safe Memory Cache:</span>
                <span className="font-semibold text-emerald-700">Active Fallback ✓</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">In-Memory Audit Records:</span>
                <span className="font-mono font-bold text-slate-900">
                  {diagnostic?.database?.totalInMemoryRecords ?? records.length} records
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Persisted Tables:</span>
                <span className="font-mono text-[10px] text-slate-600">jobs, quotes, job_notifications</span>
              </div>
            </div>
          </div>

          <div className="mt-3 p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-100 text-[11px] text-emerald-900">
            <div className="font-bold flex items-center gap-1 mb-0.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>Zero Notification Loss:</span>
            </div>
            <p className="leading-snug text-[10px] text-emerald-800">
              Authoritative PostgreSQL writes backed by non-blocking recovery scheduler for complete delivery guarantees.
            </p>
          </div>
        </div>
      </div>

      {/* 2b. Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Dispatched */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Total Dispatches</span>
            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
              <RotateCw className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900">{metrics?.total || records.length}</div>
          <p className="text-[11px] text-slate-500 mt-1">Across Email, SMS &amp; WhatsApp</p>
        </div>

        {/* Card 2: Transactional Email */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Email (Resend / SendGrid)</span>
            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Mail className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-900">{metrics?.emailSent || 0}</span>
            <span className="text-xs font-semibold text-rose-600">
              {metrics?.emailFailed || 0} failed
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Branded HTML &amp; plain text templates</p>
        </div>

        {/* Card 3: Nigerian SMS */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Nigerian SMS (SMSLive247)</span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
              <Smartphone className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-900">{metrics?.smsSent || 0}</span>
            <span className="text-xs font-semibold text-rose-600">
              {metrics?.smsFailed || 0} failed
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Single-SMS bounds with 234 normalization</p>
        </div>

        {/* Card 4: WhatsApp Cloud API */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">WhatsApp Cloud API</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-900">{metrics?.whatsappSent || 0}</span>
            <span className="text-xs font-semibold text-rose-600">
              {metrics?.whatsappFailed || 0} failed
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Interactive quick quotes &amp; bot assist</p>
        </div>
      </div>

      {/* 3. Provider Diagnostics & Live Dispatch Test Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Email Box */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">Direct Email Dispatch Test</h3>
              <p className="text-[11px] text-slate-500">
                Dispatches a live branded JobMatch NG job match alert via configured provider (Resend, SendGrid, etc.)
              </p>
            </div>
          </div>

          <form onSubmit={handleSendTestEmail} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Recipient Email Address</label>
              <input
                type="email"
                required
                placeholder="e.g. your-email@gmail.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Artisan / Recipient Name</label>
              <input
                type="text"
                placeholder="e.g. Emeka Okonkwo"
                value={testEmailName}
                onChange={(e) => setTestEmailName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <button
              type="submit"
              disabled={testEmailLoading || !testEmail}
              className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
            >
              {testEmailLoading ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span>{testEmailLoading ? 'Sending via Provider...' : 'Send Live Test Email'}</span>
            </button>
          </form>

          {testEmailResult && (
            <div
              className={`mt-4 p-3 rounded-xl border text-xs ${
                testEmailResult.success
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}
            >
              <div className="flex items-center gap-1.5 font-bold mb-1">
                {testEmailResult.success ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                )}
                <span>{testEmailResult.success ? 'EMAIL SENT SUCCESSFULLY' : 'Email Dispatch Result'}</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                {testEmailResult.success
                  ? `Message ID: ${testEmailResult.messageId || 'Accepted by Resend'}`
                  : (testEmailResult.errorMessage || testEmailResult.error || 'Failed to dispatch email.')}
              </p>
            </div>
          )}
        </div>

        {/* Test SMS Box */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700">
              <Smartphone className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">Direct Nigerian SMS Dispatch Test</h3>
              <p className="text-[11px] text-slate-500">
                Dispatches a live SMS alert to any Nigerian network (MTN, Airtel, Glo, 9mobile) via SMSLive247
              </p>
            </div>
          </div>

          <form onSubmit={handleSendTestSms} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Recipient Nigerian Phone</label>
              <input
                type="text"
                required
                placeholder="e.g. 08034567890 or +234 803 456 7890"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Artisan / Recipient Name</label>
              <input
                type="text"
                placeholder="e.g. Osasere Idehen"
                value={testPhoneName}
                onChange={(e) => setTestPhoneName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <button
              type="submit"
              disabled={testPhoneLoading || !testPhone}
              className="w-full py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
            >
              {testPhoneLoading ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span>{testPhoneLoading ? 'Sending via SMS Gateway...' : 'Send Live Test SMS'}</span>
            </button>
          </form>

          {testPhoneResult && (
            <div
              className={`mt-4 p-3 rounded-xl border text-xs ${
                testPhoneResult.success
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}
            >
              <div className="flex items-center gap-1.5 font-bold mb-1">
                {testPhoneResult.success ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                )}
                <span>{testPhoneResult.success ? 'SMS Dispatched Successfully' : 'SMS Dispatch Result'}</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                {testPhoneResult.errorMessage || testPhoneResult.error || `Provider Message ID: ${testPhoneResult.messageId}`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 3b. Customer Notification Lifecycle Simulator & Test Console */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center text-teal-700">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">Customer 12-Stage Lifecycle Notification Tester</h3>
              <p className="text-[11px] text-slate-500">
                Test multi-channel notifications (Email + SMSLive247) across every customer job lifecycle milestone
              </p>
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-lg bg-teal-100/60 text-teal-800 text-[10px] font-bold uppercase tracking-wider self-start sm:self-auto">
            12 Lifecycle Events
          </span>
        </div>

        <form onSubmit={handleSendTestCustomerEvent} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Customer Lifecycle Event</label>
            <select
              value={testCustEventType}
              onChange={(e) => setTestCustEventType(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white font-medium text-slate-800"
            >
              <option value="JOB_POSTED">1. JOB_POSTED</option>
              <option value="PROFESSIONAL_MATCHED">2. PROFESSIONAL_MATCHED</option>
              <option value="NEW_QUOTE_RECEIVED">3. NEW_QUOTE_RECEIVED</option>
              <option value="MULTIPLE_QUOTES_AVAILABLE">4. MULTIPLE_QUOTES_AVAILABLE</option>
              <option value="PROFESSIONAL_SELECTED">5. PROFESSIONAL_SELECTED</option>
              <option value="JOB_ACCEPTED">6. JOB_ACCEPTED</option>
              <option value="JOB_DECLINED">7. JOB_DECLINED</option>
              <option value="JOB_SCHEDULED">8. JOB_SCHEDULED</option>
              <option value="JOB_STARTED">9. JOB_STARTED</option>
              <option value="JOB_COMPLETED">10. JOB_COMPLETED</option>
              <option value="JOB_CANCELLED">11. JOB_CANCELLED</option>
              <option value="PROFESSIONAL_CANCELLED">12. PROFESSIONAL_CANCELLED</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Customer Name</label>
            <input
              type="text"
              required
              value={testCustName}
              onChange={(e) => setTestCustName(e.target.value)}
              placeholder="e.g. Tunde Adeleke"
              className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Customer Email</label>
            <input
              type="email"
              required
              value={testCustEmail}
              onChange={(e) => setTestCustEmail(e.target.value)}
              placeholder="customer@example.com"
              className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Customer Phone (Nigerian)</label>
            <div className="flex gap-2">
              <input
                type="text"
                required
                value={testCustPhone}
                onChange={(e) => setTestCustPhone(e.target.value)}
                placeholder="08031112233"
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                type="submit"
                disabled={testCustLoading || !testCustEmail || !testCustPhone}
                className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 cursor-pointer transition-all disabled:opacity-50 shrink-0"
              >
                {testCustLoading ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span>Test Event</span>
              </button>
            </div>
          </div>
        </form>

        {testCustResult && (
          <div className="mt-4 p-4 rounded-xl border border-teal-200 bg-teal-50/70 text-xs">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal-700" />
                <span className="font-bold text-teal-900">
                  Lifecycle Event Dispatched: {testCustResult.summary?.eventType || testCustEventType}
                </span>
              </div>
              <span className="text-[11px] text-teal-700 font-semibold">
                Customer: {testCustResult.summary?.customerName || testCustName}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div className="p-2.5 rounded-lg bg-white border border-teal-100">
                <div className="flex items-center gap-1.5 font-bold text-slate-800 text-[11px] mb-1">
                  <Mail className="w-3.5 h-3.5 text-indigo-600" />
                  <span>Email Channel Result</span>
                </div>
                <div className="text-[11px] text-slate-600">
                  Status: <strong>{testCustResult.summary?.emailResult?.status || 'dispatched'}</strong>
                  {testCustResult.summary?.emailResult?.provider && (
                    <span> ({testCustResult.summary.emailResult.provider})</span>
                  )}
                  {testCustResult.summary?.emailResult?.error && (
                    <span className="text-rose-600 block mt-0.5">{testCustResult.summary.emailResult.error}</span>
                  )}
                </div>
              </div>

              <div className="p-2.5 rounded-lg bg-white border border-teal-100">
                <div className="flex items-center gap-1.5 font-bold text-slate-800 text-[11px] mb-1">
                  <Smartphone className="w-3.5 h-3.5 text-amber-600" />
                  <span>SMS Channel Result</span>
                </div>
                <div className="text-[11px] text-slate-600">
                  Status: <strong>{testCustResult.summary?.smsResult?.status || 'dispatched'}</strong>
                  {testCustResult.summary?.smsResult?.provider && (
                    <span> ({testCustResult.summary.smsResult.provider})</span>
                  )}
                  {testCustResult.summary?.smsResult?.error && (
                    <span className="text-rose-600 block mt-0.5">{testCustResult.summary.smsResult.error}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 4. Filter Toolbar & Search */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search recipient, artisan name, job ID or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            type="submit"
            className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold shrink-0 cursor-pointer"
          >
            Search
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Filter className="w-3.5 h-3.5" />
            <span>Channel:</span>
          </div>
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            className="px-2.5 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold bg-white text-slate-700"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
          </select>

          <div className="flex items-center gap-1.5 text-xs text-slate-500 ml-2">
            <span>Status:</span>
          </div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-2.5 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold bg-white text-slate-700"
          >
            <option value="all">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* 5. Notifications Audit Logs Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-black text-slate-900">Notification Logs &amp; Audit Trail</h3>
          <span className="text-xs text-slate-500">{records.length} records retrieved</span>
        </div>

        {records.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <h4 className="text-sm font-bold text-slate-700">No notification dispatches found</h4>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              When a customer creates a job or requests a quote, matched artisans will receive automated Email, SMS, and WhatsApp alerts recorded here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500 border-b border-slate-200 font-bold uppercase tracking-wider text-[10px]">
                  <th className="py-3 px-4">Channel</th>
                  <th className="py-3 px-4">Recipient &amp; Artisan</th>
                  <th className="py-3 px-4">Type / Subject</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Provider</th>
                  <th className="py-3 px-4">Time</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4">
                      {r.channel === 'email' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-extrabold uppercase">
                          <Mail className="w-3 h-3" /> Email
                        </span>
                      )}
                      {r.channel === 'sms' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-extrabold uppercase">
                          <Smartphone className="w-3 h-3" /> SMS
                        </span>
                      )}
                      {r.channel === 'whatsapp' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-extrabold uppercase">
                          <MessageSquare className="w-3 h-3" /> WhatsApp
                        </span>
                      )}
                    </td>

                    <td className="py-3 px-4">
                      <div className="font-bold text-slate-900">{r.recipient_name || 'Artisan'}</div>
                      <div className="text-[11px] font-mono text-slate-500">{r.recipient}</div>
                    </td>

                    <td className="py-3 px-4 max-w-xs truncate">
                      <div className="font-semibold text-slate-800 truncate">{r.subject || r.notification_type}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.message}</div>
                      {r.job_id && (
                        <span className="inline-block mt-0.5 font-mono text-[10px] text-slate-400">
                          Job: #{r.job_id.slice(0, 8)}
                        </span>
                      )}
                    </td>

                    <td className="py-3 px-4">
                      {r.status === 'sent' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-extrabold">
                          <CheckCircle2 className="w-3 h-3" /> Sent
                        </span>
                      )}
                      {r.status === 'delivered' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-extrabold">
                          <CheckCircle2 className="w-3 h-3" /> Delivered
                        </span>
                      )}
                      {r.status === 'failed' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-extrabold">
                          <XCircle className="w-3 h-3" /> Failed
                        </span>
                      )}
                      {(r.status === 'pending' || r.status === 'queued') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-extrabold">
                          <Clock className="w-3 h-3" /> Queued
                        </span>
                      )}
                    </td>

                    <td className="py-3 px-4">
                      <span className="font-mono text-[11px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                        {r.provider}
                      </span>
                    </td>

                    <td className="py-3 px-4 text-[11px] text-slate-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} •{' '}
                      {new Date(r.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                    </td>

                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setSelectedRecordForDetail(r)}
                          className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold cursor-pointer"
                        >
                          Details
                        </button>
                        {r.status === 'failed' && (
                          <button
                            onClick={() => handleRetry(r.id)}
                            disabled={actionPendingId === r.id}
                            className="px-2 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-[11px] font-bold flex items-center gap-1 cursor-pointer disabled:opacity-50"
                          >
                            <RotateCw className={`w-2.5 h-2.5 ${actionPendingId === r.id ? 'animate-spin' : ''}`} />
                            <span>Retry</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 6. Diagnostics Modal */}
      {selectedRecordForDetail && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="font-black text-sm text-slate-900">Notification Detail</span>
                <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-slate-100">
                  {selectedRecordForDetail.channel}
                </span>
              </div>
              <button
                onClick={() => setSelectedRecordForDetail(null)}
                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs"
              >
                ✕
              </button>
            </div>

            <div className="py-4 space-y-3 text-xs">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Recipient</span>
                <span className="font-bold text-slate-800">
                  {selectedRecordForDetail.recipient_name} ({selectedRecordForDetail.recipient})
                </span>
              </div>

              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Type &amp; Subject</span>
                <span className="font-semibold text-slate-800">
                  {selectedRecordForDetail.subject || selectedRecordForDetail.notification_type}
                </span>
              </div>

              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Message Content</span>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-slate-700 whitespace-pre-wrap font-sans text-[11px] max-h-40 overflow-y-auto">
                  {selectedRecordForDetail.message}
                </div>
              </div>

              {selectedRecordForDetail.error_message && (
                <div className="bg-rose-50 p-3 rounded-xl border border-rose-200 text-rose-900">
                  <span className="text-[10px] uppercase font-bold text-rose-700 block mb-0.5">
                    Provider Error Details ({selectedRecordForDetail.error_code})
                  </span>
                  <p className="text-[11px] leading-relaxed">{selectedRecordForDetail.error_message}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                <div>
                  Provider: <strong>{selectedRecordForDetail.provider}</strong>
                </div>
                <div>
                  Status: <strong>{selectedRecordForDetail.status.toUpperCase()}</strong>
                </div>
                <div>
                  Dispatched: <strong>{new Date(selectedRecordForDetail.created_at).toLocaleTimeString()}</strong>
                </div>
                {selectedRecordForDetail.provider_message_id && (
                  <div className="truncate">
                    ID: <strong className="font-mono">{selectedRecordForDetail.provider_message_id}</strong>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
              {selectedRecordForDetail.status === 'failed' && (
                <button
                  onClick={() => {
                    handleRetry(selectedRecordForDetail.id);
                    setSelectedRecordForDetail(null);
                  }}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs"
                >
                  Retry Dispatch
                </button>
              )}
              <button
                onClick={() => setSelectedRecordForDetail(null)}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
